import { useEffect, useRef, useState, ReactNode, SyntheticEvent } from 'react';
import { useNavigate } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Toolbar from '@mui/material/Toolbar';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ToggleButton, ToggleButtonGroup, Divider, ListItem, ListItemIcon, ListItemText, Button } from '@mui/material';

// project import
import Search from './Search';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import MainCard from 'components/MainCard';
import CardContent from '@mui/material/CardContent';
import { ThemeMode, TextSize } from 'config';
import useAuthEntra from 'hooks/useAuthPharo';
import { useUserInfo } from 'hooks/useUserInfo';
import useConfig from 'hooks/useConfig';
import OrderGridSettingsSection from 'components/OrderGridSettingsSection';
import { isDesktopShell } from 'utils/isDesktopShell';
import appConfig from 'appConfig';
import { getCurrentAppType } from 'utils/dynamicConfig';

// assets
import MoreOutlined from '@ant-design/icons/MoreOutlined';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import ExpandOutlined from '@ant-design/icons/ExpandOutlined';
import CompressOutlined from '@ant-design/icons/CompressOutlined';
import SunOutlined from '@ant-design/icons/SunOutlined';
import MoonOutlined from '@ant-design/icons/MoonOutlined';
import DesktopOutlined from '@ant-design/icons/DesktopOutlined';
import ZoomInOutlined from '@ant-design/icons/ZoomInOutlined';
import ZoomOutOutlined from '@ant-design/icons/ZoomOutOutlined';
import ReloadOutlined from '@ant-design/icons/ReloadOutlined';

// ==============================|| HEADER CONTENT - MOBILE ||============================== //

// Zoom utility functions (same as in Profile component)
const ZOOM_STORAGE_KEY = 'app-zoom-level';

const getZoomContainer = (): HTMLElement => {
  // Target the main content area more specifically
  const mainContent = document.querySelector('.MuiBox-root') || 
                     document.querySelector('[role="main"]') ||
                     document.querySelector('#root') || 
                     document.body;
  return mainContent as HTMLElement;
};

const getCurrentZoom = (): number => {
  try {
    const container = getZoomContainer();
    
    // Method 1: Use zoom property (Chrome/Edge)
    const zoomValue = (container.style as any).zoom;
    if (zoomValue) {
      return parseFloat(zoomValue);
    }
    
    // Method 2: Use CSS transform scale
    const transform = container.style.transform;
    if (transform && transform.includes('scale(')) {
      const match = transform.match(/scale\(([^)]+)\)/);
      if (match) {
        return parseFloat(match[1]);
      }
    }
    
    return 1;
  } catch (e) {
    return 1;
  }
};

const setZoom = (zoomLevel: number): void => {
  try {
    const container = getZoomContainer();
    
    // Method 1: Use zoom property (Chrome/Edge)
    (container.style as any).zoom = zoomLevel.toString();
  } catch (e) {
    try {
      // Method 2: Use CSS transform scale with proper containment
      const container = getZoomContainer();
      container.style.transform = `scale(${zoomLevel})`;
      container.style.transformOrigin = 'top left';
      container.style.overflow = 'hidden'; // Prevent overflow
    } catch (e2) {
      // Method 3: Fallback to body zoom (less ideal)
      console.warn('Container zoom not supported, falling back to body zoom');
      (document.body.style as any).zoom = zoomLevel.toString();
    }
  }
};

const saveZoomLevel = (zoomLevel: number): void => {
  try {
    localStorage.setItem(ZOOM_STORAGE_KEY, zoomLevel.toString());
  } catch (e) {
    console.warn('Could not save zoom level to localStorage');
  }
};

// Helper function to get current subdomain
const getCurrentSubdomain = (): string => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  return parts.length >= 3 ? parts[0] : parts[0];
};

export default function MobileSection() {
  const theme = useTheme();
  const navigate = useNavigate();

  const { isAuthenticated, user, logout } = useAuthEntra();
  const { userFullName, userName, userRoles, lookups, isUserContextLoading, isUserOverridden, signedInFullName, signedInUserName } = useUserInfo();
  const { mode, onChangeMode, onIsComactViewChange, isCompactView, textSize, onChangeTextSize } = useConfig();
  const shell = isDesktopShell();
  const appType = getCurrentAppType();
  const isOpsDomain = appType.includes('ops');
  const [settingsVersion, setSettingsVersion] = useState(0);

  const handleSettingsChange = () => {
    setSettingsVersion(prev => prev + 1);
  };

  // Zoom control functions
  const handleZoomIn = () => {
    const currentZoom = getCurrentZoom();
    const newZoom = Math.min(3, currentZoom + 0.1); // Max 300%
    setZoom(newZoom);
    saveZoomLevel(newZoom);
  };

  const handleZoomOut = () => {
    const currentZoom = getCurrentZoom();
    const newZoom = Math.max(0.1, currentZoom - 0.1); // Min 10%
    setZoom(newZoom);
    saveZoomLevel(newZoom);
  };

  const handleZoomReset = () => {
    setZoom(1);
    saveZoomLevel(1);
  };

  // Function to format version string
  const formatVersion = (versionString: string): string => {
    if (!versionString) return 'v?';
    
    // Extract version from format like "react-oms@1.0.141.51471+c90f7b0375"
    const match = versionString.match(/@(\d+\.\d+\.\d+)/);
    if (match) {
      return `v${match[1]}`;
    }
    
    return 'v?';
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(`/login`, {
        state: {
          from: ''
        }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleThemeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue !== null) {
      onChangeMode(newValue as ThemeMode);
    }
  };

  const handleCompactChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue !== null) {
      onIsComactViewChange(newValue === 'compact');
    }
  };

  const handleTextSizeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    // Allow deselection by clicking on currently selected button
    if (newValue === null) {
      onChangeTextSize(TextSize.NONE);
    } else {
      onChangeTextSize(newValue as TextSize);
    }
  };

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<any>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'grey.200' : 'grey.300';
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.100';

  return (
    <>
      <Box sx={{ flexShrink: 0, ml: 0.75 }}>
        <IconButton
          sx={{ color: 'text.primary', bgcolor: open ? iconBackColorOpen : iconBackColor }}
          aria-label="open user settings"
          ref={anchorRef}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          color="secondary"
          variant="light"
        >
          <MoreOutlined />
        </IconButton>
      </Box>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={{ boxShadow: theme.customShadows.z1, width: 290, minWidth: 240, maxWidth: { xs: 250, md: 290 } }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 1.5, pb: 2 }}>
                    {/* User Info Section */}
                    <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Grid item>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Stack>
                            { isUserOverridden && 
                              <Typography variant="h6" color={"gray"}>{ `${signedInFullName} as: `}</Typography>
                            }
                            <Typography variant="h5">{userFullName}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {userRoles ? userRoles[0] : 'N/A'}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                    </Grid>

                    <Divider sx={{ mb: 2 }} />

                    {/* Theme Toggle Section */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Theme
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        value={mode}
                        onChange={handleThemeChange}
                        size="small"
                        fullWidth
                      >
                        <ToggleButton value={ThemeMode.LIGHT} aria-label="Light Theme">
                          <SunOutlined />
                        </ToggleButton>
                        <ToggleButton value={ThemeMode.DARK} aria-label="Dark Theme">
                          <MoonOutlined />
                        </ToggleButton>
                        <ToggleButton value={ThemeMode.CLASSIC} aria-label="Classic Theme">
                          <DesktopOutlined />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Compact Mode Toggle Section */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Layout
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        value={isCompactView ? 'compact' : 'normal'}
                        onChange={handleCompactChange}
                        size="small"
                        fullWidth
                      >
                        <ToggleButton value="normal" aria-label="Normal Layout">
                          <ExpandOutlined />
                        </ToggleButton>
                        <ToggleButton value="compact" aria-label="Compact Layout">
                          <CompressOutlined />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Text Size Toggle Section */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Text Size
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        value={textSize === TextSize.NONE ? null : textSize}
                        onChange={handleTextSizeChange}
                        size="small"
                        fullWidth
                      >
                        <ToggleButton value={TextSize.LARGE} aria-label="Large Text">
                          <span className="text-size-button" style={{ fontSize: '18px', fontWeight: 'bold', lineHeight: 1, fontFamily: 'inherit' }}>T</span>
                        </ToggleButton>
                        <ToggleButton value={TextSize.MEDIUM} aria-label="Medium Text">
                          <span className="text-size-button" style={{ fontSize: '14px', fontWeight: 'bold', lineHeight: 1, fontFamily: 'inherit' }}>T</span>
                        </ToggleButton>
                        <ToggleButton value={TextSize.SMALL} aria-label="Small Text">
                          <span className="text-size-button" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: 1, fontFamily: 'inherit' }}>T</span>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Zoom Controls Section - Only show in desktop shell */}
                    {shell && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Zoom
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleZoomOut}
                            startIcon={<ZoomOutOutlined />}
                            sx={{ flex: 1 }}
                          >
                            Out
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleZoomReset}
                            startIcon={<ReloadOutlined />}
                            sx={{ flex: 1 }}
                          >
                            Reset
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleZoomIn}
                            startIcon={<ZoomInOutlined />}
                            sx={{ flex: 1 }}
                          >
                            In
                          </Button>
                        </Stack>
                      </Box>
                    )}

                    {/* Order Grid Settings Section - Only show in desktop shell and non-ops domains */}
                    {shell && !isOpsDomain && (
                      <OrderGridSettingsSection onSettingsChange={handleSettingsChange} />
                    )}

                    <Divider sx={{ mb: 2 }} />

                    {/* Logout Section */}
                    <ListItem 
                      button 
                      onClick={handleLogout}
                      sx={{ 
                        borderRadius: 1,
                        '&:hover': { 
                          backgroundColor: theme.palette.mode === ThemeMode.DARK ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)' 
                        }
                      }}
                    >
                      <ListItemIcon>
                        <LogoutOutlined />
                      </ListItemIcon>
                      <ListItemText primary="Sign Out" />
                    </ListItem>

                    {/* App Version Section */}
                    <Box sx={{ mt: 2, textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatVersion(appConfig.version)}
                      </Typography>
                    </Box>
                  </CardContent>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
}
